import { ChangeEvent, useState, useMemo, useCallback } from 'react';
import { JobCountDetailResponse } from '@doda/api-client/aspida/@types';
import { findJobCount } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import { IndustryDrillDownType } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/IndustryDrillDown';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useAppSelector } from 'redux/hooks';
import { IndustryData, InitialIndustryDataType } from '../../types';

interface ArgumentType {
  initialIndustryData: InitialIndustryDataType;
  initialDrillDown?: IndustryDrillDownType | null;
}

/**
 * 業種に関するhooks
 * @param param.initialIndustryData 整形された業種マスタ
 * @param param.initialDrillDown ドリルダウン
 * @returns 表示用配列と更新処理
 */
export const useIndustry = ({
  initialIndustryData,
  initialDrillDown,
}: ArgumentType) => {
  const { preBtn } = useAppSelector(appQueryParamsSelector);
  const isDrillDown = preBtn === '2';
  const [industryLList, setIndustryLList] = useState(
    initialIndustryData.lData ?? []
  );

  const [industrySList, setIndustrySList] = useState(
    initialIndustryData.sData ?? []
  );

  const industryDisplayList: IndustryData[] = useMemo(() => {
    return industryLList.map((industryL) => ({
      ...industryL,
      industrySList: industrySList.filter(
        ({ parentId }) => parentId === industryL.id
      ),
    }));
  }, [industryLList, industrySList]);

  // 選択中の大分類のid配列
  const selectedIndustryLIdList = useMemo(() => {
    return industryLList
      .filter(({ isChecked }) => isChecked)
      .map(({ id }) => id);
  }, [industryLList]);

  // 選択中の小分類のid配列
  const selectedIndustrySIdList = useMemo(() => {
    return industrySList
      .filter(({ isChecked }) => isChecked)
      .map(({ id }) => id);
  }, [industrySList]);

  // 大分類のチェックボックス更新処理
  const updateIsCheckedIndustryLList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = industryLList.map((industry) => {
        if (industry.id !== selectedId) return industry;

        return {
          ...industry,
          isChecked: event.target.checked,
        };
      });

      setIndustryLList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [industryLList]
  );

  // 小分類のチェックボックス更新処理
  const updateIsCheckedIndustrySList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = industrySList.map((industry) => {
        if (industry.id !== selectedId) return industry;

        return {
          ...industry,
          isChecked: event.target.checked,
        };
      });

      setIndustrySList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [industrySList]
  );

  // 大分類の求人件数更新処理
  const updateIndustryLListJobCount = useCallback(
    (
      industryLIdList: JobCountDetailResponse[] | undefined,
      parentId?: string | undefined
    ) => {
      setIndustryLList((pre) =>
        pre.map((industry) => {
          /**
           * 大分類にチェックが入っている場合、
           * 小分類にチェックが入っていない場合はファセット件数を更新しない
           */
          const existCheckedSList = industry.id === parentId;
          if (!industry.isChecked && !existCheckedSList) return industry;

          return {
            ...industry,
            jobCount: findJobCount(industryLIdList, industry.id),
          };
        })
      );
    },
    []
  );

  // 小分類の求人件数更新処理
  const updateIndustrySListJobCount = useCallback(
    (
      industrySIdList: JobCountDetailResponse[] | undefined,
      industryLIdList: JobCountDetailResponse[] | undefined
    ) => {
      setIndustrySList((pre) =>
        pre.map((industry) => {
          // チェックが入っていない場合は更新しない
          if (!industry.isChecked) return industry;

          /**
           * 小分類にチェックが入っている場合、
           * 押下したチェックボックスが小分類ではない場合は
           * 大分類のファセット件数を更新する
           */
          updateIndustryLListJobCount(industryLIdList, industry.parentId);

          return {
            ...industry,
            jobCount: findJobCount(industrySIdList, industry.id),
          };
        })
      );
    },
    [updateIndustryLListJobCount]
  );

  // ドリルダウン
  const [industryDrillDown, setIndustryDrillDown] = useState(initialDrillDown);

  // ドリルダウンの選択中の大分類Idリスト
  const selectedIndustryDrillDownLIdList = industryDrillDown?.isChecked
    ? [industryDrillDown.id]
    : [];

  // ドリルダウンの選択中のidリスト
  const selectedIndustryDrillDownSIdList = useMemo(() => {
    if (industryDrillDown) {
      return industryDrillDown.list
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    }

    return [];
  }, [industryDrillDown]);

  // ドリルダウンのチェックボックス更新処理
  const updateDrillDownIsChecked = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      if (initialDrillDown && industryDrillDown) {
        const checkedList = industryDrillDown.list.map((industry) => {
          if (industry.id !== selectedId) return industry;

          return {
            ...industry,
            isChecked: event.target.checked,
          };
        });

        const updatedIndustryDrillDown = {
          ...initialDrillDown,
          isChecked: checkedList.every((item) => !item.isChecked),
          list: checkedList ?? [],
        };

        setIndustryDrillDown(updatedIndustryDrillDown);

        return {
          industryLId: updatedIndustryDrillDown.isChecked
            ? [updatedIndustryDrillDown.id]
            : [],
          industrySId: checkedList
            .filter(({ isChecked }) => isChecked)
            .map(({ id }) => id),
        };
      }

      return undefined;
    },
    [industryDrillDown, initialDrillDown]
  );

  const updateIndustryJobCount = (
    lCount: JobCountDetailResponse[] | undefined,
    sCount: JobCountDetailResponse[] | undefined,
    isToggleIndustry: boolean
  ) => {
    if (isToggleIndustry) return;
    updateIndustryLListJobCount(lCount);
    updateIndustrySListJobCount(sCount, lCount);
  };

  return {
    industryDisplayList,
    industryDrillDown,
    selectedIndustryLIdList: isDrillDown
      ? selectedIndustryDrillDownLIdList
      : selectedIndustryLIdList,
    selectedIndustrySIdList: isDrillDown
      ? selectedIndustryDrillDownSIdList
      : selectedIndustrySIdList,
    updateIsCheckedIndustryLList,
    updateIsCheckedIndustrySList,
    updateIndustryJobCount,
    updateDrillDownIsChecked,
  };
};
